import React, { useState, useCallback, useMemo } from 'react';
import { Col, Row } from 'antd';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import AddDrawer from '../common/addDrawer';

import Analytics from '../helpers/Analytics';
import { getHighestNumber, sortByCode } from '../helpers/helpers';

import ProjectAddGlobalCostCodeDrawer from './ProjectAddGlobalCostCodeDrawer';
import ProjectAddCostCodeTemplate from './ProjectAddCostCodeTemplate';
import CostCodeEditView from '../costcodes/CostCodeEditView';
import { formatCustomFieldCreatePayload } from '../helpers/costcodeHelpers';
import OnTraccrSwitch from '../common/inputs/OnTraccrSwitch';
import CostcodeUploadList from '../costcodes/costcodeUploadList';
import OnTraccrButton from '../common/buttons/OnTraccrButton';

export default function ProjectAddCostCode({
  isNotDisplay,
  divisionId,
  chosen = [],
  settings: {
    suggestNumbers,
  } = {},
  templateView,
  onCostcodeAdd,
  onCostcodeEdit,
  showList,
  onPhaseChanged,
  onCheckChanged,
  onApplyTemplate,
  onRemove,
}) {
  const { t } = useTranslation();

  const [visible, setVisible] = useState(false);
  const [showGlobal, setShowGlobal] = useState(false);
  const [errors, setErrors] = useState({});
  const [customFields, setCustomFields] = useState([]);

  const initialCode = suggestNumbers ? (getHighestNumber(chosen, 'code') + 1).toString() : null;

  const onAddNew = useCallback(() => {
    setVisible(true);
  }, []);

  const onAddExisting = useCallback(() => {
    setShowGlobal(true);
  }, []);

  const CostCodeAddView = useCallback((form, formProps) => (
    <CostCodeEditView
      form={form}
      formProps={{
        key: chosen.length,
        costcodes: chosen,
        code: initialCode,
        categoryIds: [],
        ...formProps,
      }}
      isProjectAdd
      errors={errors}
      customFields={customFields}
      setCustomFields={setCustomFields}
    />
  ), [chosen, initialCode, errors, customFields]);

  const onAdd = useCallback((values) => {
    if (values) {
      // Global add
      if (Array.isArray(values)) {
        setShowGlobal(false);
        const valuesWithCategoryAssignments = values.map((value) => {
          const { categoryIds } = value;
          const newPayload = { ...value, categoryAssignments: {} };
          categoryIds?.forEach((categoryIdToAdd) => {
            const isCustomCategory = typeof categoryIdToAdd === 'string';
            newPayload.categoryAssignments[categoryIdToAdd] = {
              defaultCategoryId: isCustomCategory ? null : categoryIdToAdd,
              customCategoryId: isCustomCategory ? categoryIdToAdd : null,
            };
          });
          return newPayload;
        });
        onCostcodeAdd(valuesWithCategoryAssignments);
        return;
      }
      Analytics.track('Costcode/Create/ProjectSpecific/FromProjectPage');

      // Add new cost code
      const payload = formatCustomFieldCreatePayload({
        payload: values,
        customFields,
      });

      if (payload?.error || !payload) {
        setErrors(payload?.errorMap);
        return;
      }

      const { categoryIds } = payload;

      const newPayload = { ...payload, categoryAssignments: {} };

      // construct category assignments
      categoryIds?.forEach((categoryIdToAdd) => {
        const isCustomCategory = typeof categoryIdToAdd === 'string';
        newPayload.categoryAssignments[categoryIdToAdd] = {
          defaultCategoryId: isCustomCategory ? null : categoryIdToAdd,
          customCategoryId: isCustomCategory ? categoryIdToAdd : null,
        };
      });

      onCostcodeAdd([newPayload]);
    }

    setErrors({});
    setVisible(false);
  }, [onCostcodeAdd, customFields]);

  const sortedByCode = useMemo(() => [...chosen].sort(sortByCode('code')), [chosen]);

  return (
    <div className="project-add-costcode-container">
      {(isNotDisplay) && (
        <Row style={{ width: '100%' }}>
          {!templateView && (
            <Col>
              <OnTraccrSwitch
                onChange={onCheckChanged}
                style={{ margin: '10px 5px' }}
                value={showList}
                title="Cost Code Required?"
              />
            </Col>
          )}
          <Col flex="auto" />
          {(showList || templateView) && (
            <>
              <Col style={{ marginRight: 8 }}>
                <OnTraccrButton
                  style={{ marginTop: 5, marginBottom: 10 }}
                  title="Add New Cost Code"
                  onClick={onAddNew}
                />
              </Col>
              <Col style={{ marginRight: 8 }}>
                <OnTraccrButton
                  style={{ marginTop: 5, marginBottom: 10 }}
                  title="Add Existing Cost Code"
                  onClick={onAddExisting}
                />
              </Col>
            </>
          )}
          {!templateView && (
            <Col>
              <ProjectAddCostCodeTemplate
                divisionId={divisionId}
                hasCostcodes={chosen && chosen.length}
                applyTemplate={onApplyTemplate}
              />
            </Col>
          )}
        </Row>
      )}
      {(showList || !isNotDisplay || templateView) && (
        <div
          style={{
            border: 'lightgray 0.25px solid',
            borderRadius: 4,
          }}
        >
          <CostcodeUploadList
            dataSource={sortedByCode}
            phased
            phaseClickable={isNotDisplay}
            onPhaseChanged={onPhaseChanged}
            onRemove={isNotDisplay ? onRemove : null}
            onEdit={isNotDisplay ? onCostcodeEdit : null}
            shouldSort={false}
            conflictList={sortedByCode}
            categories
            // Need this prop to calculate conflicts across code
          />
        </div>
      )}
      <AddDrawer
        onClose={onAdd}
        visible={visible}
        title={`Add New Cost Code to ${t('Project')}`}
        isAdd
        formView={CostCodeAddView}
        formProps={{
          editing: true,
        }}
        width={800}
      />
      <ProjectAddGlobalCostCodeDrawer
        visible={showGlobal}
        onClose={() => setShowGlobal(false)}
        divisionId={divisionId}
        chosen={sortedByCode}
        onSubmit={onAdd}
      />
    </div>
  );
}

ProjectAddCostCode.propTypes = {
  isNotDisplay: PropTypes.bool,
  divisionId: PropTypes.string,
  chosen: PropTypes.array, // eslint-disable-line react/forbid-prop-types
  settings: PropTypes.shape({
    suggestNumbers: PropTypes.bool,
  }),
  templateView: PropTypes.bool,
  onCostcodeAdd: PropTypes.func,
  onCostcodeEdit: PropTypes.func,
  showList: PropTypes.bool,
  onPhaseChanged: PropTypes.func,
  onCheckChanged: PropTypes.func,
  onApplyTemplate: PropTypes.func,
  onRemove: PropTypes.func,
};

ProjectAddCostCode.defaultProps = {
  isNotDisplay: false,
  divisionId: null,
  chosen: [],
  settings: PropTypes.shape({
    suggestNumbers: false,
  }),
  templateView: false,
  onCostcodeAdd: undefined,
  onCostcodeEdit: undefined,
  showList: false,
  onPhaseChanged: undefined,
  onCheckChanged: undefined,
  onApplyTemplate: undefined,
  onRemove: undefined,
};
